import { service } from '@ember/service';

import type {
	APIData,
	CreateRecordProperties,
	RecordData,
	RequestError,
	Service,
} from 'webapp-types';
import {
	BillingFeature,
	ModelName,
	NewReportSlideFragmentLayout,
	NewReportSlideLayout,
	ReportSlideFragmentType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getOrderFromPosition } from 'webapp/utils/common';

import BoardPopupVisualSettingsComponent from 'webapp/components/board/popup-visual-settings';

import type Report from 'webapp/models-custom/report';
import ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import type ReportSlideRecord from 'webapp/models/report-slide';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

type DisabledNewSlideLayoutsToFeatureMap = Partial<Record<NewReportSlideLayout, BillingFeature>>;

export default class ModelManagerReportSlidesService extends ModelManagerForStoreService<
	ReportSlideRecord,
	ReportSlide
> {
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('socket') declare socketService: Service.Socket;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;

	defaultReportSlideColor = '#ffffff';
	defaultReportSlideThemeId = 1;

	getRecordsForReport(reportId: number): ReportSlideRecord[] {
		return this.savedRecords.filter((item) => item.reportId === reportId);
	}

	get disabledNewSlideLayoutsForCurrentPlan(): DisabledNewSlideLayoutsToFeatureMap {
		const disabled: DisabledNewSlideLayoutsToFeatureMap = {};

		// only data-story can be disabled as slide layout
		// other limitations will be per-fragment
		if (!this.billingService.visualizationsAdvancedEnabled) {
			disabled[NewReportSlideLayout.DATA_STORY] = BillingFeature.VISUALIZATIONS_ADVANCED;
		}

		return disabled;
	}

	async createFromBoard(boardId: number, reportId: number): Promise<ReportSlideRecord> {
		const slide = this.createNewModel({
			reportId,
		});
		slide.order = (slide.report?.maxOrder || -1) + 1;

		return slide.save().then(async (slideRecord) => {
			// create fragment
			const slide = this.findModel(slideRecord.id!);
			if (slide) {
				const fragment = this.mmReportSlideFragmentsService.createFragmentForSlide(
					slide.numericId!,
					ReportSlideFragmentType.BOARD,
					boardId,
				);
				fragment.order = getOrderFromPosition(1, 1);
				// await to avoid duplicate record with same id and board rendering twice on pageless
				await fragment.saveAndCatch();
			}
			return slideRecord;
		});
	}

	async duplicate(slide: ReportSlide, reportId?: number): Promise<ReportSlideRecord> {
		return this.requestService
			.api<APIData.ReportSlide.RecordResponse>('POST', `/report-slides/${slide.id}/clone`, {
				reportId,
			})
			.then((reply) => {
				// We change the order of the cloned slide, to put it below the original one. After we execute this part, the slides will be sorted by "order" and "id"
				// in "duplicateSlide()" at ReportsEditorComponent, where this method was called from. Sorted slides are then passed to Reports Service method "reorderSlides",
				// where we save sorted state of the slides and send it back to BE.
				if (slide.reportId === reportId && reply.reportSlide) {
					reply.reportSlide.order = slide.order;
				}
				this.storeService.pushPayload(reply);
				return this.findRecord(String(reply.reportSlide?.id))!;
			});
	}

	async createNewFromLayout(report: Report, layout: NewReportSlideLayout, slideOrder?: number) {
		const isBoardSlide = layout === NewReportSlideLayout.BOARD;

		const targetOrder = slideOrder ?? report.maxOrder + 1;

		const saveSlideFn = async (order: number, boardId?: number): Promise<ReportSlideRecord> => {
			const properties = {
				reportId: report.numericId!,
				order,
			};

			this.mmReportsService.triggerReportEdited();

			const slide = this.createNewModel(properties);

			let nameForTracking = '';
			switch (layout) {
				case NewReportSlideLayout.COVER:
					nameForTracking = 'cover';
					break;
				case NewReportSlideLayout.BOARD:
					nameForTracking = 'board';
					break;
				case NewReportSlideLayout.COLUMNS_1:
					nameForTracking = '1_column';
					break;
				case NewReportSlideLayout.COLUMNS_2:
					nameForTracking = '2_columns';
					break;
				case NewReportSlideLayout.COLUMNS_3:
					nameForTracking = '3_columns';
					break;
				case NewReportSlideLayout.DATA_STORY:
					nameForTracking = 'data_story';
					break;
			}
			this.analyticsService.event(TrackingEvent.NEW_SLIDE_ADDED, {
				type: nameForTracking,
			});

			const slideRecord = await slide.save();

			// create fragments
			if (slide) {
				const slideId = slide.numericId!;
				const fragments: ReportSlideFragment[] = [];
				if (isBoardSlide) {
					const boardFragment = this.mmReportSlideFragmentsService.createFragmentForSlide(
						slideId,
						ReportSlideFragmentType.BOARD,
						boardId,
					);
					const newOrder = getOrderFromPosition(1, 1);
					boardFragment.order = newOrder;
					fragments.push(boardFragment);
				} else {
					const isCoverLayout = layout === NewReportSlideLayout.COVER,
						titleFragmentOrder = getOrderFromPosition(isCoverLayout ? 2 : 1, 1);

					const titleFragment = this.mmReportSlideFragmentsService.createFragmentForSlide(
						slideId,
						ReportSlideFragmentType.TITLE,
					);
					titleFragment.order = titleFragmentOrder;
					fragments.push(titleFragment);

					this.mmReportSlideFragmentsService.trackDatablockDrop(
						ReportSlideFragmentType.TITLE,
					);

					let placeholderCount = 0;
					if (layout === NewReportSlideLayout.COLUMNS_1) {
						placeholderCount = 1;
					} else if (layout === NewReportSlideLayout.COLUMNS_2) {
						placeholderCount = 2;
					} else if (layout === NewReportSlideLayout.COLUMNS_3) {
						placeholderCount = 3;
					}

					if (placeholderCount) {
						const width = 100 / placeholderCount;

						for (let i = 1; i <= placeholderCount; i++) {
							const fragment =
								this.mmReportSlideFragmentsService.createFragmentForSlide(
									slideId,
									ReportSlideFragmentType.PLACEHOLDER,
								);
							fragment.order = getOrderFromPosition(2, i);
							fragment.width = width;
							fragments.push(fragment);
						}
					}

					if (layout === NewReportSlideLayout.DATA_STORY) {
						await this.mmReportSlideFragmentsService
							.addFragmentFromLayout(NewReportSlideFragmentLayout.DATA_STORY, slide)
							.then(({ fragment, afterFragmentCreateFn }) => {
								fragment.order = getOrderFromPosition(2, 1);
								fragment
									.save()
									.then(afterFragmentCreateFn)
									.catch(
										this.requestService.showAllValidationErrors.bind(
											this.requestService,
										),
									);
							})
							.catch(() => {
								// do nothing, user probably closed popup
							});
					} else if (isCoverLayout) {
						const imageFragment =
							this.mmReportSlideFragmentsService.createFragmentForSlide(
								slideId,
								ReportSlideFragmentType.IMAGE,
							);
						imageFragment.order = getOrderFromPosition(1, 1);
						fragments.push(imageFragment);
						this.mmReportSlideFragmentsService.trackDatablockDrop(
							ReportSlideFragmentType.IMAGE,
						);

						// don't add story_text fragment on pageless cover slide
						if (!report.isPageless) {
							// empty story_text, without metric
							const storyFragment =
								this.mmReportSlideFragmentsService.createFragmentForSlide(
									slideId,
									ReportSlideFragmentType.STORY_TEXT,
								);
							storyFragment.order = getOrderFromPosition(3, 1);
							fragments.push(storyFragment);
							this.mmReportSlideFragmentsService.trackDatablockDrop(
								ReportSlideFragmentType.STORY_TEXT,
							);
						}
					}
				}
				await Promise.all(fragments.map((f) => f.saveAndCatch()));
			}
			return slideRecord;
		};

		let promise: Promise<ReportSlideRecord>;
		if (isBoardSlide) {
			promise = new Promise((resolve, reject) => {
				const popup = this.mmBoardsService.createBoardsSelectListModal();

				popup.open({
					model: this.mmBoardsService.normalBoardModels,
					maxSelectable: report.slidesTypeBoardRemainingCount,
					isReport: true,
					onConfirm: async (selected: number[]) => {
						for (let i = 0; i < selected.length; i++) {
							const boardId = selected[i];
							const slideRecord = await saveSlideFn(targetOrder + i, boardId).catch(
								this.requestService.showAllValidationErrors.bind(
									this.requestService,
								),
							);
							resolve(slideRecord!);
						}
						return Promise.resolve(true);
					},
					onCancel: reject,
				});
			});
		} else {
			promise = saveSlideFn(targetOrder).then((reportSlideRecord) => {
				return reportSlideRecord;
			});

			promise.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}

		// always resolve - check for reportSlideRecord argument after
		void promise.catch();

		return promise;
	}

	openVisualSettingsPopup(model: ReportSlide) {
		this.popupService
			.createModal(BoardPopupVisualSettingsComponent, {
				popupClass: 'wallvisualpopup',
				allowStacked: true, // for image-upload/popup
			})
			.open({
				model,
				hideThemes: true,
			});
	}

	getReportSlideDataWithToken(
		token: string,
		reportSlideId: string,
		sendData: APIData.Datawall.DataRequest = {},
	): Promise<RecordData.Payload> {
		let url = `/token/${token}/report-slide/${reportSlideId}`;

		return new Promise((resolve, reject) => {
			this.requestService
				.api<RecordData.Payload>('GET', url, sendData)
				.then((reply) => {
					this.storeService.pushPayload(reply);

					const board = reply.boards?.[0];
					if (board) {
						this.mmBoardsService.markBoardRequested(String(board.id));
					}

					void this.socketService.afterDataChannelSubscribed
						?.then<unknown>(() => {
							if (board && !this.navigationService.isDatawallApp) {
								// for templates, we still subscribe to space-user-board channel
								return this.socketService.subscribeToBoardData(+board.id);
							} else {
								// on datawall, we already have channel with token
								return Promise.resolve();
							}
						})
						.then(() => {
							url += '/data';
							this.requestService
								.api<RecordData.Payload>('GET', url, sendData)
								.then((samplesReply) => {
									this.storeService.pushPayload(samplesReply);
								})
								.catch(
									this.requestService.handleGenericThrownError.bind(
										this.requestService,
									),
								);
						})
						.catch();

					resolve(reply);
				})
				.catch((errorData: RequestError) => {
					this.requestService.showAllValidationErrors(errorData);
					reject();
				});
		});
	}

	reorderReportLocally(report: Report, slideIds: number[]) {
		report.slides.forEach((slide) => {
			slide.directPropUpdate({
				order: slideIds.indexOf(slide.numericId!),
			});
		});
	}

	commitReorderForReport(report: Report, slideIds: number[]) {
		this.reorderReportLocally(report, slideIds);

		this.requestService
			.api('POST', `/reports/${report.id}/order`, slideIds)
			.catch(this.requestService.showGenericValidationError.bind(this.requestService));
	}

	getSlideFallbackImage(bgTheme: string): string {
		const typeText = 'text';
		return `/images/reports/fallback-${typeText}-${bgTheme}bg.png`;
	}

	// ------ For-store ------

	modelName = ModelName.REPORT_SLIDE;

	createModel(model: ReportSlideRecord): ReportSlide {
		return new ReportSlide({ model });
	}

	override createNewModel(
		properties: CreateRecordProperties<ReportSlideRecord> = {},
	): ReportSlide {
		properties = this.useGlobalVisualSettings(
			properties,
			this.accountService.globalVisualSettingsForReportSlide,
		);

		if (typeof properties.color === 'undefined') {
			const report = this.mmReportsService.normalReportModels.find(
					(item) => item.numericId === properties.reportId,
				),
				firstSlideTypeNotBoard = report?.slidesTypeNotBoard[0];

			if (firstSlideTypeNotBoard) {
				if (report?.isColorAppliedToAllSlidesTypeNotBoard) {
					properties.color = firstSlideTypeNotBoard?.color;
					properties.themeId = firstSlideTypeNotBoard?.themeId;
				} else {
					properties.color = this.defaultReportSlideColor;
					properties.themeId = this.defaultReportSlideThemeId;
				}

				if (report?.isBackgroundAppliedToAllSlidesTypeNotBoard) {
					properties.bg = firstSlideTypeNotBoard?.bg || '';
				}

				if (report?.isLogoAppliedToAllSlidesTypeNotBoard) {
					properties.logo = firstSlideTypeNotBoard?.logo || '';
				}

				if (report?.isBackgroundOpacityAppliedToAllSlidesTypeNotBoard) {
					properties.bgOpacity =
						firstSlideTypeNotBoard?.bgOpacity ||
						this.mmBoardsService.defaultBgImageOpacity;
				}
			} else {
				properties.color = this.defaultReportSlideColor;
				properties.themeId = this.defaultReportSlideThemeId;
			}
		}
		if (!properties.title) {
			properties.title = 'My Slide';
		}

		return super.createNewModel(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/report-slides': ModelManagerReportSlidesService;
	}
}
